import { useEffect, useMemo, useState } from 'react';
import { Hash, Pencil, Plus, Trash2, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { TooltipProvider } from '@/components/ui/tooltip';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import type { CoverageItem, KeywordMatchMode, MediaChannel, TrackedKeyword } from '@/client/types/media';
import { ActionIconButton } from './ActionIconButton';
import { useApiTrackedKeywords } from '../coverage-api';
import {
  useCreateTrackedKeywordMutation,
  useDeleteTrackedKeywordMutation,
  useUpdateTrackedKeywordMutation,
} from '@/client/api/marketing/sources';
import { getApiErrorMessage, getDuplicateTopic } from '@/client/utils/errors';
import {
  formatKeywordTerms,
  itemMatchesKeyword,
  suggestKeywordLabel,
} from '../keyword-match';

interface KeywordTrackerPanelProps {
  /** Mentions (or other items) used to compute live match counts */
  items: CoverageItem[];
  /** Currently selected keyword ids used as feed filters */
  selectedIds: Set<string>;
  onSelectedIdsChange: (next: Set<string>) => void;
  /** When set, only list keywords whose channels include this value */
  channel?: MediaChannel;
}

export function KeywordTrackerPanel({
  items,
  selectedIds,
  onSelectedIdsChange,
  channel,
}: KeywordTrackerPanelProps) {
  const { keywords: allKeywords } = useApiTrackedKeywords();
  const removeKeyword = useDeleteTrackedKeywordMutation();
  const keywords = useMemo(
    () =>
      channel
        ? allKeywords.filter((kw) => kw.channels.includes(channel))
        : allKeywords,
    [allKeywords, channel],
  );
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<TrackedKeyword | null>(null);
  const [deleting, setDeleting] = useState<TrackedKeyword | null>(null);

  const matchCounts = useMemo(() => {
    const map = new Map<string, number>();
    for (const kw of keywords) {
      map.set(
        kw.id,
        items.filter((item) => itemMatchesKeyword(item, kw)).length,
      );
    }
    return map;
  }, [keywords, items]);

  const toggleFilter = (id: string) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onSelectedIdsChange(next);
  };

  return (
    <div className="rounded-xl border border-gray-100 bg-white p-3 shadow-sm">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">
            Tracked keywords & hashtags
          </p>
          <p className="text-xs text-gray-500">
            Add single terms or combinations (AND / OR). Click a chip to filter mentions.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {selectedIds.size > 0 && (
            <button
              type="button"
              onClick={() => onSelectedIdsChange(new Set())}
              className="text-[11px] font-medium text-blue-700 hover:underline"
            >
              Clear keyword filter
            </button>
          )}
          <Button
            type="button"
            size="sm"
            className="gap-1"
            onClick={() => {
              setEditing(null);
              setFormOpen(true);
            }}
          >
            <Plus className="h-3.5 w-3.5" /> Add keyword
          </Button>
        </div>
      </div>

      {keywords.length === 0 ? (
        <p className="py-4 text-center text-sm text-gray-500">
          No tracked keywords yet. Add a word, hashtag, or combination to start filtering.
        </p>
      ) : (
        <div className="flex flex-wrap gap-2">
          <TooltipProvider delayDuration={200}>
          {keywords.map((kw) => {
            const active = selectedIds.has(kw.id);
            const count = matchCounts.get(kw.id) ?? 0;
            return (
              <div
                key={kw.id}
                className={`inline-flex items-center gap-1 rounded-full border py-1 pl-1 pr-1 text-xs transition ${
                  active
                    ? 'border-violet-300 bg-violet-50 ring-2 ring-violet-200 ring-offset-1'
                    : kw.enabled
                      ? 'border-gray-200 bg-white'
                      : 'border-dashed border-gray-200 bg-gray-50 opacity-70'
                }`}
              >
                <button
                  type="button"
                  onClick={() => toggleFilter(kw.id)}
                  className="inline-flex items-center gap-1.5 rounded-full py-0.5 pl-1.5 pr-2 text-left hover:bg-black/5"
                  title={formatKeywordTerms(kw)}
                >
                  <span className="flex h-6 w-6 items-center justify-center rounded-full bg-violet-100 text-violet-700">
                    <Hash className="h-3.5 w-3.5" />
                  </span>
                  <span>
                    <span className="block font-medium text-gray-900">{kw.label}</span>
                    <span className="block text-[10px] text-gray-500">
                      {kw.matchMode === 'all' ? 'AND' : 'OR'} · {count} match
                      {count === 1 ? '' : 'es'}
                      {!kw.enabled ? ' · paused' : ''}
                    </span>
                    <span className="mt-0.5 flex flex-wrap gap-1">
                      {kw.channels.map((ch) => (
                        <span
                          key={ch}
                          className="rounded bg-gray-100 px-1 py-px text-[9px] font-medium uppercase tracking-wide text-gray-600"
                        >
                          {ch}
                        </span>
                      ))}
                    </span>
                  </span>
                </button>
                <ActionIconButton
                  label="Edit"
                  size="sm"
                  onClick={() => {
                    setEditing(kw);
                    setFormOpen(true);
                  }}
                  className="rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-700"
                >
                  <Pencil className="h-3 w-3" />
                </ActionIconButton>
                <ActionIconButton
                  label="Remove"
                  size="sm"
                  onClick={() => setDeleting(kw)}
                  className="rounded-full text-gray-400 hover:bg-red-50 hover:text-red-600"
                >
                  <Trash2 className="h-3 w-3" />
                </ActionIconButton>
              </div>
            );
          })}
          </TooltipProvider>
        </div>
      )}

      <KeywordFormDialog
        open={formOpen}
        keyword={editing}
        defaultChannel={channel}
        onClose={() => {
          setFormOpen(false);
          setEditing(null);
        }}
      />

      <AlertDialog open={!!deleting} onOpenChange={(v) => !v && setDeleting(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove tracked keyword?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleting && (
                <>
                  Stop tracking <strong>{deleting.label}</strong> (
                  {formatKeywordTerms(deleting)}). Mentions stay in the feed; this only removes
                  the tracker.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              onClick={() => {
                if (!deleting) return;
                removeKeyword.mutate(
                  { tkid: Number(deleting.id) },
                  {
                    onError: (err) =>
                      toast.error(getApiErrorMessage(err, 'Could not remove the tracker')),
                  },
                );
                if (selectedIds.has(deleting.id)) {
                  const next = new Set(selectedIds);
                  next.delete(deleting.id);
                  onSelectedIdsChange(next);
                }
                toast.success('Keyword removed (mock)');
                setDeleting(null);
              }}
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function KeywordFormDialog({
  open,
  keyword,
  defaultChannel,
  onClose,
}: {
  open: boolean;
  keyword: TrackedKeyword | null;
  defaultChannel?: MediaChannel;
  onClose: () => void;
}) {
  const isEdit = !!keyword;
  const createKeyword = useCreateTrackedKeywordMutation();
  const updateKeyword = useUpdateTrackedKeywordMutation();
  const [label, setLabel] = useState('');
  const [terms, setTerms] = useState<string[]>([]);
  const [draft, setDraft] = useState('');
  const [matchMode, setMatchMode] = useState<KeywordMatchMode>('any');
  const [enabled, setEnabled] = useState(true);
  const [channels, setChannels] = useState<MediaChannel[]>(['social']);

  useEffect(() => {
    if (!open) return;
    if (keyword) {
      setLabel(keyword.label);
      setTerms([...keyword.terms]);
      setMatchMode(keyword.matchMode);
      setEnabled(keyword.enabled);
      setChannels([...keyword.channels]);
    } else {
      setLabel('');
      setTerms([]);
      setMatchMode('any');
      setEnabled(true);
      setChannels(defaultChannel ? [defaultChannel] : ['social']);
    }
    setDraft('');
  }, [open, keyword, defaultChannel]);

  const toggleChannel = (ch: MediaChannel) => {
    setChannels((prev) => {
      if (prev.includes(ch)) {
        if (prev.length === 1) return prev;
        return prev.filter((c) => c !== ch);
      }
      return [...prev, ch];
    });
  };

  const addTerm = (raw: string) => {
    const pieces = raw
      .split(/[,]+/)
      .map((t) => t.trim())
      .filter(Boolean);
    if (pieces.length === 0) return;
    setTerms((prev) => {
      const next = [...prev];
      for (const p of pieces) {
        if (!next.some((t) => t.toLowerCase() === p.toLowerCase())) next.push(p);
      }
      return next;
    });
    setDraft('');
  };

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (terms.length === 0) {
      toast.error('Add at least one keyword or hashtag');
      return;
    }
    if (channels.length === 0) {
      toast.error('Select at least one channel');
      return;
    }
    // `enabled` is TINYINT server-side: send 0/1, not true/false, so a tracker
    // read back looks exactly like one just written.
    const payload = {
      label: label.trim() || suggestKeywordLabel(terms, matchMode),
      terms,
      match_mode: matchMode,
      channels,
      enabled: enabled ? 1 : 0,
    };
    const handlers = {
      onSuccess: () => {
        toast.success(isEdit ? 'Tracker updated' : 'Tracker added');
        onClose();
      },
      onError: (err: unknown) => {
        const dup = getDuplicateTopic(err);
        if (dup) {
          toast.error(`Already watched (topic ${dup.tkid})`);
          return;
        }
        toast.error(getApiErrorMessage(err, 'Could not save the tracker'));
      },
    };
    if (isEdit && keyword) {
      updateKeyword.mutate({ tkid: Number(keyword.id), ...payload }, handlers);
    } else {
      createKeyword.mutate(payload, handlers);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit tracked keyword' : 'Track keyword / hashtag'}</DialogTitle>
          <DialogDescription>
            Use a single word, a hashtag (#FairfulSummer), or combine multiple terms with AND / OR.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4">
          <label className="block space-y-1.5">
            <span className="text-xs font-medium text-gray-600">Label (optional)</span>
            <input
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              className="h-9 w-full rounded-md border border-gray-200 px-3 text-sm outline-none ring-blue-500 focus:ring-2"
              placeholder={suggestKeywordLabel(terms, matchMode)}
            />
          </label>

          <div className="space-y-1.5">
            <span className="text-xs font-medium text-gray-600">Terms</span>
            <div className="flex flex-wrap gap-1.5 rounded-md border border-gray-200 bg-white p-2">
              {terms.map((t) => (
                <span
                  key={t}
                  className="inline-flex items-center gap-1 rounded-full bg-violet-50 px-2 py-0.5 text-xs font-medium text-violet-800"
                >
                  {t}
                  <button
                    type="button"
                    onClick={() => setTerms((prev) => prev.filter((x) => x !== t))}
                    className="rounded-full p-0.5 hover:bg-violet-100"
                    aria-label={`Remove ${t}`}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
              <input
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ',') {
                    e.preventDefault();
                    addTerm(draft);
                  }
                  if (e.key === 'Backspace' && !draft && terms.length) {
                    setTerms((prev) => prev.slice(0, -1));
                  }
                }}
                onBlur={() => {
                  if (draft.trim()) addTerm(draft);
                }}
                className="min-w-[140px] flex-1 border-0 bg-transparent px-1 py-0.5 text-sm outline-none"
                placeholder={terms.length ? 'Add another…' : 'Type Fairful or #hashtag, Enter'}
              />
            </div>
            <p className="text-[11px] text-gray-400">
              Press Enter or comma to add. Prefix with # for hashtags.
            </p>
          </div>

          <div className="space-y-1.5">
            <span className="text-xs font-medium text-gray-600">Combination</span>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setMatchMode('any')}
                className={`flex-1 rounded-md border px-3 py-2 text-left text-xs ${
                  matchMode === 'any'
                    ? 'border-violet-300 bg-violet-50 text-violet-900'
                    : 'border-gray-200 text-gray-600'
                }`}
              >
                <span className="font-semibold">Any term (OR)</span>
                <span className="mt-0.5 block text-[11px] opacity-80">
                  Match if at least one term appears
                </span>
              </button>
              <button
                type="button"
                onClick={() => setMatchMode('all')}
                className={`flex-1 rounded-md border px-3 py-2 text-left text-xs ${
                  matchMode === 'all'
                    ? 'border-violet-300 bg-violet-50 text-violet-900'
                    : 'border-gray-200 text-gray-600'
                }`}
              >
                <span className="font-semibold">All terms (AND)</span>
                <span className="mt-0.5 block text-[11px] opacity-80">
                  Match only when every term appears
                </span>
              </button>
            </div>
          </div>

          <div className="space-y-1.5">
            <span className="text-xs font-medium text-gray-600">Channels</span>
            <div className="flex flex-wrap gap-3">
              {(['social', 'traditional'] as MediaChannel[]).map((ch) => (
                <label key={ch} className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={channels.includes(ch)}
                    onChange={() => toggleChannel(ch)}
                    className="h-4 w-4 rounded border-gray-300"
                  />
                  <span className="capitalize">{ch}</span>
                </label>
              ))}
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300"
            />
            Enabled (count toward tracking & filters)
          </label>

          {isEdit && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                if (!keyword) return;
                const next = !enabled;
                updateKeyword.mutate(
                  { tkid: Number(keyword.id), enabled: next ? 1 : 0 },
                  {
                    onSuccess: () => {
                      setEnabled(next);
                      toast.message(next ? 'Tracking resumed' : 'Tracking paused');
                    },
                    onError: (err) =>
                      toast.error(getApiErrorMessage(err, 'Could not change the tracker')),
                  },
                );
              }}
            >
              {enabled ? 'Pause tracking' : 'Resume tracking'}
            </Button>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit">{isEdit ? 'Save' : 'Add tracker'}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
